import { scenarioConfigs } from "../config/scenarios";
import type {
  CabinState,
  DecisionSummary,
  RuleOperator,
  ScenarioConfig,
  ScenarioDecision,
  TriggerRule
} from "../types/domain";

function compare(operator: RuleOperator, current: unknown, expected?: string | number | boolean) {
  if (operator === "truthy") return Boolean(current);
  if (operator === "equals") return current === expected;
  if (operator === "notEquals") return current !== expected;
  if (operator === "gte") return Number(current) >= Number(expected);
  if (operator === "lte") return Number(current) <= Number(expected);
  return false;
}

function evaluateRule(state: CabinState, rule: TriggerRule) {
  const current = state[rule.field];
  return compare(rule.operator, current, rule.value);
}

export function isScenarioEligible(state: CabinState, scenario: ScenarioConfig) {
  return scenario.rules.every((rule) => !rule.required || evaluateRule(state, rule));
}

function evaluateScenario(state: CabinState, scenario: ScenarioConfig): Omit<ScenarioDecision, "rank"> {
  const matchedRules = scenario.rules.filter((rule) => evaluateRule(state, rule));
  const eligible = isScenarioEligible(state, scenario);
  const score = eligible ? Math.min(
    100,
    matchedRules.reduce((total, rule) => total + rule.points, 0)
  ) : 0;

  return {
    scenario,
    score,
    active: eligible && score >= scenario.threshold,
    reasons: (eligible ? matchedRules : []).map((rule) => rule.reason)
  };
}

export function buildFallbackCopy(decision: ScenarioDecision | null) {
  if (!decision) {
    return "服务待命中，当前条件未达到主动提醒阈值。";
  }

  const topReasons = decision.reasons.slice(0, 2).join("，");
  return `${decision.scenario.title}建议已就绪：${topReasons || "当前条件适合主动提醒"}。`;
}

export function computeDecisionSummary(state: CabinState): DecisionSummary {
  const ranked = scenarioConfigs
    .map((scenario) => evaluateScenario(state, scenario))
    .sort((a, b) => b.score - a.score)
    .map((decision, index) => ({
      ...decision,
      rank: index + 1
    }));

  const topDecision = ranked[0];
  const activeDecision = ranked.find((decision) => decision.active) ?? null;
  return {
    decisions: ranked,
    topDecision,
    activeDecision,
    activeCount: ranked.filter((decision) => decision.active).length,
    generatedCopy: buildFallbackCopy(activeDecision),
    copySource: "local"
  };
}
